import SumsumApi from '@/api/SumsumApi';
import DDLogs from '@/DDLogs';
import consts from '../utilities/constants';

const state = {
  components: {},
  loading: {},
  errors: {},
};

const getters = {
  isComponentLoading: state => name => state.loading[name] || false,
  getComponentError: state => name => state.errors[name] || null,
  hasComponent: state => name => !!state.components[name],
  getComponentByName: state => name => state.components[name] || null,
  getComponentStateVariables: state => name => {
    const comp = state.components[name];
    if (!comp || !comp.stateVariables) {
      return {};
    }
    const filteredVariables = {};
    Object.keys(comp.stateVariables).forEach(key => {
      const value = comp.stateVariables[key];
      if (value !== '') {
        filteredVariables[key] = value;
      }
    });
    return filteredVariables;
  },
  getStateVariableByName: state => ({ componentName, variableName }) => {
    const comp = state.components[componentName];
    const value = comp && comp.stateVariables ? comp.stateVariables[variableName] : undefined;
    return value === '' ? undefined : value;
  },
};

const actions = {
  async getComponentByName({ dispatch, state }, { componentName, extraParams = {}, isUnauthenticated = false }) {
    if (state.components[componentName]) {
      return state.components[componentName];
    }

    return await dispatch('fetchDynamicComponent', { componentName, extraParams, isUnauthenticated });
  },

  async fetchDynamicComponent({ commit, state }, { componentName, extraParams = {}, isUnauthenticated = false }) {
    commit('setLoading', { componentName, loading: true });
    commit('setError', { componentName, error: null });

    try {
      const component = isUnauthenticated
        ? await SumsumApi.getDynamicComponentByNameForUnauthenticatedUser(componentName)
        : await SumsumApi.getDynamicComponentByNameForCustomer(componentName, extraParams);

      if (component && component.pages) {
        const imageUrls = extractImageUrls(component.pages);
        await preloadImages(imageUrls);
        commit('setComponent', { componentName, component });
        commit('setLoading', { componentName, loading: false });
        return component;
      }
      throw new Error(`Component ${componentName} has no pages or invalid structure`);
    } catch (error) {
      DDLogs.error('Error fetching dynamic component', { componentName, error });
      commit('setError', { componentName, error });
      commit('setLoading', { componentName, loading: false });
      throw error;
    }
  },

  async preloadAllDynamicComponents({ dispatch }) {
    const componentNames = Object.values(consts.DYNAMIC_COMPONENT_NAMES);
    await dispatch('preloadDynamicComponent', componentNames);
  },

  async preloadDynamicComponent({ dispatch, commit, state }, componentNames) {
    const componentNamesArray = Array.isArray(componentNames) ? componentNames : [componentNames];

    for (const componentName of componentNamesArray) {
      if (!state.components[componentName]) {
        try {
          await dispatch('fetchDynamicComponent', { componentName });
        } catch (error) {
          console.warn(`Failed to preload component ${componentName}:`, error);
        }
      }
    }
  },

  setStateVariableByName({ commit, state }, { componentName, variableName, value }) {
    if (!state.components[componentName]) {
      return;
    }
    commit('setComponentStateVariable', { componentName, variableName, value });
  },
};

const mutations = {
  setComponent(state, { componentName, component }) {
    state.components = {
      ...state.components,
      [componentName]: component,
    };
  },

  setComponentStateVariable(state, { componentName, variableName, value }) {
    const existingComponent = state.components[componentName];
    if (!existingComponent) {
      return;
    }
    const normalizedValue = value === '' ? undefined : value;
    const updatedComponent = {
      ...existingComponent,
      stateVariables: {
        ...(existingComponent.stateVariables || {}),
        [variableName]: normalizedValue,
      },
    };
    state.components = {
      ...state.components,
      [componentName]: updatedComponent,
    };
  },

  setLoading(state, { componentName, loading }) {
    state.loading = {
      ...state.loading,
      [componentName]: loading,
    };
  },

  setError(state, { componentName, error }) {
    state.errors = {
      ...state.errors,
      [componentName]: error,
    };
  },
};

function extractImageUrls(pages) {
  const imageUrls = [];

  if (!pages || !Array.isArray(pages)) {
    return imageUrls;
  }

  pages.forEach(page => {
    if (page.content && page.content.widgets) {
      const widgetImageUrls = extractImageUrlsFromWidgets(page.content.widgets);
      imageUrls.push(...widgetImageUrls);
    }
  });

  return [...new Set(imageUrls)];
}

function extractImageUrlsFromWidgets(widgets) {
  const imageUrls = [];

  if (!widgets || !Array.isArray(widgets)) {
    return imageUrls;
  }

  widgets.forEach(widget => {
    if (widget.type === 'image' && widget.url) {
      imageUrls.push(widget.url);
    }
  });

  return imageUrls;
}

function preloadImages(imageUrls) {
  return Promise.all(
    imageUrls.map(url => new Promise(resolve => {
      const img = new Image();
      img.onload = resolve;
      img.onerror = () => {
        console.warn(`Failed to preload image: ${url}`);
        resolve();
      };
      img.src = url;
    })),
  );
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
